import type { CrmStatus, GetLeadsQuery, LeadConsentStatus, LeadSourceType, EventFundingType } from '@/lib/api/schemas';

import { MAX_VISIBLE_COLUMNS } from '@/constants/tables';

interface HeaderState {
  all: string[];
  visible: string[];
  maxVisible: number;
}

export interface Option<T = string> {
  id: T;
  label: string;
  value: string;
}

export interface GetLeadsFilterQuery {
  createdAtFrom?: string | null;
  createdAtTo?: string | null;
  mandataries: Option[];
  leadEvents: Option[];
  providers: Option[];
  crmStatus: Option<CrmStatus>[];
  consentStatus: Option<LeadConsentStatus>[];
  postalCodes: string[];
  sourceTypes: Option<LeadSourceType>[];
  fundingTypes: Option<EventFundingType>[];
  HasNoR1In3Month?: boolean;
  IsWinner?: boolean;
  includeOav?: boolean;
};

interface TableState {
  headers: HeaderState;
  filter: GetLeadsFilterQuery;
}

export const useLeadsStore = defineStore('leadFilter', {
  state: (): TableState => ({
    headers: {
      all: [],
      visible: [],
      maxVisible: MAX_VISIBLE_COLUMNS,
    },
    filter: {
      createdAtFrom: undefined,
      createdAtTo: undefined,
      mandataries: [],
      leadEvents: [],
      providers: [],
      crmStatus: [],
      consentStatus: [],
      postalCodes: [],
      sourceTypes: [],
      fundingTypes: [],
      HasNoR1In3Month: undefined,
      IsWinner: undefined,
      includeOav: undefined,
    },
  }),
  actions: {
    initHeaders(allHeaders: string[], defaultVisible: string[], maxVisible = MAX_VISIBLE_COLUMNS) {
      const persistedVisible = this.headers?.visible ?? [];
      const persistedIsValid
        = persistedVisible.length > 0
          && persistedVisible.every(k => allHeaders.includes(k));

      this.headers = {
        all: allHeaders,
        visible: persistedIsValid
          ? persistedVisible
          : defaultVisible.slice(0, maxVisible),
        maxVisible,
      };
    },

    setVisibleHeaders(visible: string[]) {
      this.headers.visible = visible.slice(0, this.headers.maxVisible);
    },
    resetFilter() {
      this.setFilter({
        createdAtFrom: undefined,
        createdAtTo: undefined,
        mandataries: [],
        leadEvents: [],
        providers: [],
        crmStatus: [],
        consentStatus: [],
        postalCodes: [],
        sourceTypes: [],
        fundingTypes: [],
        HasNoR1In3Month: undefined,
        IsWinner: undefined,
        includeOav: undefined,
      });
    },
    setFilter(filter: GetLeadsFilterQuery) {
      this.filter.createdAtFrom = filter.createdAtFrom;
      this.filter.createdAtTo = filter.createdAtTo;
      this.filter.mandataries = filter.mandataries ?? [];
      this.filter.leadEvents = filter.leadEvents ?? [];
      this.filter.providers = filter.providers ?? [];
      this.filter.crmStatus = filter.crmStatus ?? [];
      this.filter.consentStatus = filter.consentStatus ?? [];
      this.filter.postalCodes = filter.postalCodes ?? [];
      this.filter.sourceTypes = filter.sourceTypes ?? [];
      this.filter.fundingTypes = filter.fundingTypes ?? [];
      this.filter.HasNoR1In3Month = filter.HasNoR1In3Month ?? undefined;
      this.filter.IsWinner = filter.IsWinner ?? undefined;
      this.filter.includeOav = filter.includeOav ?? undefined;
    },

    isHeaderVisible(header: string): boolean {
      return this.headers.visible.includes(header);
    },

    getVisibleHeaders(): string[] {
      return this.headers.visible;
    },

    getAllHeaders(): string[] {
      return this.headers.all;
    },
    getFilter(): GetLeadsQuery | undefined {
      return {
        createdAtFrom: this.filter.createdAtFrom,
        createdAtTo: this.filter.createdAtTo,
        mandataries: this.filter.mandataries?.map(option => option.value),
        leadEvents: this.filter.leadEvents?.map(option => option.id),
        providers: this.filter.providers?.map(option => option.id),
        crmStatus: this.filter.crmStatus?.map(option => option.id),
        consentStatus: this.filter.consentStatus?.map(option => option.id),
        postalCodes: this.filter.postalCodes,
        sourceTypes: this.filter.sourceTypes?.map(option => option.id),
        fundingTypes: this.filter.fundingTypes?.map(option => option.id),
        HasNoR1In3Month: this.filter.HasNoR1In3Month ? true : undefined,
        IsWinner: this.filter.IsWinner ? true : undefined,
        IncludeOav: this.filter.includeOav ? true : undefined,
      };
    },

    hasFilter(): boolean {
      return Object.values(this.filter).some((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return !!value;
      });
    },
  },

  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
  },
});
